import { FC, useContext, useReducer, createContext, useMemo, ReactNode } from 'react'

import {
  EventSideMenuFormState,
  EventSideMenuReducerState,
  EventSideMenuReducerActionsState,
} from './types'

const State = createContext<EventSideMenuReducerState>({} as EventSideMenuReducerState)

const Actions = createContext<EventSideMenuReducerActionsState>(
  {} as EventSideMenuReducerActionsState
)

export const useStore = () => {
  const state = useContext(State)
  return state
}

export const useActions = () => {
  const actions = useContext(Actions)
  return actions
}

export const formInitialState = {
  opened: false,
  title: '',
  price: '',
  deliveryNotes: '',
  messages: [],
  checkoutPayload: {},
  tevoSectionName: null,
  longDescription: '',
  sectionIds: [],
} as EventSideMenuReducerState

const OPEN_DETAILS = 'OPEN_DETAILS'
const CLOSE_DETAILS = 'CLOSE_DETAILS'
const ADD_TO_CHECKOUT = 'ADD_TO_CHECKOUT'
const SET_SECTIONS_IDS = 'SET_SECTIONS_IDS'
const SET_MESSAGES = 'SET_MESSAGES'

type ActionType =
  | typeof OPEN_DETAILS
  | typeof CLOSE_DETAILS
  | typeof ADD_TO_CHECKOUT
  | typeof SET_SECTIONS_IDS
  | typeof SET_MESSAGES

const formStateReducer = (
  state = formInitialState,
  {
    data,
    type,
  }: {
    data?: EventSideMenuFormState
    type: ActionType
  }
) => {
  switch (type) {
    case OPEN_DETAILS:
      return {
        ...state,
        opened: true,
      }

    case SET_MESSAGES:
      return {
        ...state,
        messages: [...state.messages, ...data.messages],
      }

    case CLOSE_DETAILS:
      return {
        ...state,
        opened: false,
      }

    case ADD_TO_CHECKOUT:
      return {
        ...state,
        ...data,
        opened: true,
      }

    case SET_SECTIONS_IDS:
      return {
        ...state,
        opened: false,
        sectionIds: data.sectionIds,
      }

    default:
      return state
  }
}

type Props = {
  children?: ReactNode
}

const Provider: FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(formStateReducer, formInitialState)

  const onAddToCheckout = (data: EventSideMenuFormState) =>
    dispatch({
      type: ADD_TO_CHECKOUT,
      data,
    })

  const onClose = () =>
    dispatch({
      type: CLOSE_DETAILS,
    })

  const onOpen = () =>
    dispatch({
      type: OPEN_DETAILS,
    })

  const setMessages = (messages) =>
    dispatch({
      type: SET_MESSAGES,
      data: { messages } as EventSideMenuReducerState,
    })

  const setSectionIds = (sectionIds: string[]) =>
    dispatch({
      type: SET_SECTIONS_IDS,
      data: {
        sectionIds,
      } as EventSideMenuReducerState,
    })

  const actionValues = useMemo(
    () => ({ onAddToCheckout, onClose, onOpen, setSectionIds, setMessages }),
    []
  )

  return (
    <State.Provider value={state}>
      <Actions.Provider value={actionValues}>{children}</Actions.Provider>
    </State.Provider>
  )
}

export default Provider
